import { zodResolver } from '@hookform/resolvers/zod'
import { passwordSchema } from '@dyeatlas/shared'
import { ArrowLeft, KeyRound, Lock, Mail } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { AuthLayout } from './AuthLayout'
import { Button } from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { Notice } from '@/components/ui/misc'
import { authClient, authErrorMessage } from '@/lib/auth-client'

const requestSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
})

export function ForgotPassword() {
  const [sent, setSent] = useState(false)
  const form = useForm<z.input<typeof requestSchema>>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await authClient.requestPasswordReset({
      email: values.email,
      redirectTo: '/reset-password',
    })
    // Always reports success: whether an address is registered is not public.
    setSent(true)
  })

  return (
    <AuthLayout>
      <div className="mb-6 flex flex-col items-center text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand">
          <KeyRound className="size-6" />
        </span>
        <h1 className="mt-5 font-display text-[26px] font-semibold text-ink">Reset your password</h1>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
          Enter your email address and we&apos;ll send you a link to choose a new password.
        </p>
      </div>

      {sent ? (
        <Notice tone="positive" title="Check your inbox">
          If that address has a DyeAtlas account, a reset link is on its way. The link expires in
          one hour.
        </Notice>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Email address" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="name@example.com"
              icon={<Mail className="size-4" />}
              invalid={Boolean(form.formState.errors.email)}
              {...form.register('email')}
            />
          </Field>
          <Button type="submit" fullWidth loading={form.formState.isSubmitting}>
            Send reset link
          </Button>
        </form>
      )}

      <div className="my-5 h-px bg-line" />
      <Link
        to="/sign-in"
        className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-ink-soft hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Back to sign in
      </Link>
    </AuthLayout>
  )
}

const resetSchema = z
  .object({ password: passwordSchema, confirmPassword: z.string() })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export function ResetPassword() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token')
  const [formError, setFormError] = useState<string | null>(null)

  const form = useForm<z.input<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirmPassword: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    if (!token) return setFormError('This reset link is missing its token.')

    const { error } = await authClient.resetPassword({ newPassword: values.password, token })
    if (error) return setFormError(authErrorMessage(error))

    toast.success('Password updated. Sign in with your new password.')
    navigate('/sign-in', { replace: true })
  })

  return (
    <AuthLayout>
      <div className="mb-6 text-center">
        <h1 className="font-display text-[26px] font-semibold text-ink">Choose a new password</h1>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        {(formError || !token) && (
          <Notice tone="caution">
            {formError ?? 'This reset link is invalid or has expired. Request a new one.'}
          </Notice>
        )}

        <Field
          label="New password"
          htmlFor="password"
          error={form.formState.errors.password?.message}
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            icon={<Lock className="size-4" />}
            invalid={Boolean(form.formState.errors.password)}
            {...form.register('password')}
          />
        </Field>

        <Field
          label="Confirm new password"
          htmlFor="confirmPassword"
          error={form.formState.errors.confirmPassword?.message}
        >
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            icon={<Lock className="size-4" />}
            invalid={Boolean(form.formState.errors.confirmPassword)}
            {...form.register('confirmPassword')}
          />
        </Field>

        <Button type="submit" fullWidth loading={form.formState.isSubmitting} disabled={!token}>
          Update password
        </Button>
      </form>
    </AuthLayout>
  )
}
